<template>
  <div class="px-4 py-5 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <div class="mb-6">
        <p class="text-xs font-semibold uppercase text-bochas-rojo">
          Configuración
        </p>
        <h2 class="mt-1 text-xl font-bold text-white sm:text-2xl">
          Configuración general
        </h2>
      </div>

      <div v-if="loading" class="card-fbochas p-6 text-bochas-text-muted">
        Cargando configuración...
      </div>

      <div v-else class="space-y-4">
        <nav class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" aria-label="Secciones de configuración">
          <button
            v-for="section in settingsSections"
            :key="section.key"
            type="button"
            :aria-pressed="activeSection === section.key"
            :class="[
              'flex min-h-[5.5rem] flex-col items-start justify-between rounded-2xl border p-3 text-left transition-colors',
              activeSection === section.key
                ? 'border-bochas-rojo/70 bg-bochas-rojo/20 text-white'
                : 'border-white/10 bg-white/[0.03] text-white/65 hover:bg-white/[0.07]',
            ]"
            @click="activeSection = section.key"
          >
            <IconFbochas :name="section.icon" :size="20" />
            <span class="text-sm font-bold">{{ section.label }}</span>
          </button>
        </nav>

        <section v-if="activeSection === 'visibility'" class="card-fbochas p-4 sm:p-5">
          <div>
            <h3 class="text-base font-bold text-white">
              Visibilidad del sitio
            </h3>
            <p class="mt-1 text-xs text-bochas-text-muted">
              Elegí qué páginas y secciones se muestran públicamente.
            </p>
          </div>

          <div class="mt-5 grid gap-4 lg:grid-cols-2">
            <div
              v-for="group in visibilityGroups"
              :key="group.key"
              class="rounded-xl border border-white/10 bg-white/[0.03] p-3"
            >
              <h4 class="text-sm font-bold text-white">{{ group.label }}</h4>
              <p
                v-if="group.description"
                class="mt-1 text-xs leading-relaxed text-bochas-text-muted"
              >
                {{ group.description }}
              </p>
              <div class="mt-3 divide-y divide-white/[0.07]">
                <label
                  v-for="item in group.items"
                  :key="item.key"
                  class="flex cursor-pointer items-center justify-between gap-4 py-2.5"
                >
                  <span class="text-sm text-white">{{ item.label }}</span>
                  <input
                    v-model="visibility[item.key]"
                    type="checkbox"
                    class="peer sr-only"
                  />
                  <span class="visibility-switch" aria-hidden="true">
                    <span class="visibility-switch__thumb"></span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </section>

        <section v-else-if="activeSection === 'access'" class="card-fbochas p-4 sm:p-5">
          <div>
            <h3 class="text-base font-bold text-white">
              Accesos del administrador
            </h3>
            <p class="mt-1 text-xs text-bochas-text-muted">
              Elegí qué módulos aparecen para cada rol.
            </p>
          </div>

          <div class="mt-4 grid grid-cols-3 gap-2">
            <button
              v-for="role in adminRoleOptions"
              :key="role.value"
              type="button"
              :aria-pressed="activeAccessRole === role.value"
              :class="[
                'flex min-h-[4.5rem] flex-col items-center justify-center gap-2 rounded-xl border px-2 py-3 transition-colors',
                activeAccessRole === role.value
                  ? 'border-sky-400/40 bg-sky-500/15 text-sky-100'
                  : 'border-white/10 bg-white/[0.03] text-white/55 hover:bg-white/[0.07]',
              ]"
              @click="activeAccessRole = role.value"
            >
              <IconFbochas :name="role.icon" :size="19" />
              <span class="truncate text-xs font-bold sm:text-sm">{{ role.label }}</span>
            </button>
          </div>

          <div class="mt-4 grid gap-3 md:grid-cols-2">
            <div
              v-for="group in accessGroups"
              :key="group.key"
              class="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]"
            >
              <div class="flex items-center gap-2 border-b border-white/[0.07] px-3 py-2.5">
                <IconFbochas :name="group.icon" :size="16" class="text-white/50" />
                <h4 class="text-xs font-bold uppercase tracking-wide text-white/70">
                  {{ group.label }}
                </h4>
              </div>
              <div class="divide-y divide-white/[0.06] px-3">
                <div
                  v-for="module in group.items"
                  :key="module.key"
                  class="flex min-h-[3.25rem] items-center justify-between gap-3 py-2"
                >
                  <span class="min-w-0 truncate text-sm font-medium text-white">
                    {{ module.shortTitle || module.title }}
                  </span>
                  <label class="cursor-pointer">
                    <input
                      v-model="adminAccess[adminAccessKey(module.key, activeAccessRole)]"
                      type="checkbox"
                      :aria-label="`${module.shortTitle || module.title}: ${activeRoleLabel}`"
                      class="peer sr-only"
                    />
                    <span class="visibility-switch" aria-hidden="true">
                      <span class="visibility-switch__thumb"></span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section v-else-if="activeSection === 'social'" class="card-fbochas p-4">
          <h3 class="text-base font-bold text-white">Redes sociales</h3>
          <div class="mt-4 grid gap-3">
            <input
              v-model="form['social.whatsapp']"
              class="admin-input"
              placeholder="WhatsApp"
            />
            <input
              v-model="form['social.instagram']"
              class="admin-input"
              placeholder="Instagram"
            />
            <input
              v-model="form['social.facebook']"
              class="admin-input"
              placeholder="Facebook"
            />
          </div>
        </section>

        <section v-else-if="activeSection === 'footer'" class="card-fbochas p-4">
          <h3 class="text-base font-bold text-white">Footer</h3>
          <div class="mt-4 grid gap-3 lg:grid-cols-2">
            <input
              v-model="form['footer.aboutTitle']"
              class="admin-input lg:col-span-2"
              placeholder="Nombre de la Federación"
            />
            <textarea
              v-model="form['footer.aboutDescription']"
              rows="3"
              class="admin-input lg:col-span-2"
              placeholder="Descripción"
            ></textarea>
            <input
              v-model="form['footer.address']"
              class="admin-input lg:col-span-2"
              placeholder="Dirección"
            />
            <textarea
              v-model="form['footer.phone']"
              class="admin-input"
              rows="2"
              placeholder="Un teléfono por línea"
            ></textarea>
            <input
              v-model="form['footer.email']"
              type="email"
              class="admin-input"
              placeholder="Correo electrónico"
            />
          </div>
        </section>

        <section v-else-if="activeSection === 'seo'" class="card-fbochas p-4">
          <h3 class="text-base font-bold text-white">SEO general</h3>
          <div class="mt-4 grid gap-3">
            <label class="grid gap-1.5">
              <span class="text-xs font-medium text-bochas-text-muted">URL pública del sitio</span>
              <input
                v-model="form['seo.siteUrl']"
                type="url"
                class="admin-input"
                placeholder="https://www.ejemplo.com"
              />
            </label>
            <label class="grid gap-1.5">
              <span class="text-xs font-medium text-bochas-text-muted">Imagen al compartir</span>
              <input
                v-model="form['seo.defaultImage']"
                type="url"
                class="admin-input"
                placeholder="https://www.ejemplo.com/imagen.jpg"
              />
            </label>
            <label class="grid gap-1.5">
              <span class="text-xs font-medium text-bochas-text-muted">Título general</span>
            <input
              v-model="form['seo.defaultTitle']"
              class="admin-input"
              placeholder="Título general"
            />
            </label>
            <label class="grid gap-1.5">
              <span class="text-xs font-medium text-bochas-text-muted">Descripción general</span>
            <textarea
              v-model="form['seo.defaultDescription']"
              rows="2"
              class="admin-input"
              placeholder="Descripción general"
            ></textarea>
            </label>
          </div>
        </section>

        <section v-if="activeSection === 'seo'" class="card-fbochas p-4">
          <h3 class="text-base font-bold text-white">SEO por sección</h3>
          <div class="mt-4 grid gap-4">
            <div
              v-for="section in seoSections"
              :key="section.key"
              class="rounded-lg border border-white/10 bg-white/[0.03] p-3"
            >
              <p class="mb-3 text-sm font-bold text-white">
                {{ section.label }}
              </p>
              <div class="grid gap-3">
                <input
                  v-model="form[`seo.${section.key}Title`]"
                  class="admin-input"
                  placeholder="Título SEO"
                />
                <textarea
                  v-model="form[`seo.${section.key}Description`]"
                  rows="2"
                  class="admin-input"
                  placeholder="Descripción SEO"
                ></textarea>
              </div>
            </div>
          </div>
        </section>

        <p v-if="error" class="text-sm text-bochas-rojo">{{ error }}</p>
        <p v-if="saved" class="text-sm text-emerald-300">Guardado.</p>

        <button
          type="button"
          class="btn-bochas-rojo px-5 py-2 text-sm"
          :disabled="saving"
          @click="save"
        >
          {{ saving ? "Guardando..." : "Guardar configuración" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import {
  actualizarSiteSetting,
  listarSiteSettings,
} from "@/services/siteSettings/siteSettingsService";
import {
  crearSiteVisibility,
  guardarSiteVisibility,
  obtenerSiteVisibility,
  visibilityGroups,
} from "@/services/siteSettings/siteVisibilityService";
import {
  adminAccessKey,
  adminRoleOptions,
  configurableAdminGroups,
  crearAdminAccess,
  guardarAdminAccess,
  obtenerAdminAccess,
} from "@/services/admin/adminModulesService";

const form = reactive({});
const items = ref([]);
const loading = ref(true);
const saving = ref(false);
const saved = ref(false);
const error = ref("");
const activeSection = ref("visibility");
const activeAccessRole = ref(adminRoleOptions[0].value);
const visibility = reactive(crearSiteVisibility());
const adminAccess = reactive(crearAdminAccess());

const activeRoleLabel = computed(
  () =>
    adminRoleOptions.find((role) => role.value === activeAccessRole.value)
      ?.label || "rol",
);
const accessGroups = computed(() =>
  configurableAdminGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((module) =>
        module.roles.includes(activeAccessRole.value),
      ),
    }))
    .filter((group) => group.items.length),
);

const settingsSections = [
  { key: "visibility", label: "Visibilidad", icon: "eye" },
  { key: "access", label: "Accesos", icon: "users" },
  { key: "social", label: "Redes", icon: "link" },
  { key: "footer", label: "Footer", icon: "doc" },
  { key: "seo", label: "SEO", icon: "settings" },
];

const footerFields = [
  {
    key: "footer.aboutTitle",
    label: "Nombre de la Federación",
    defaultValue: "Federación de Bochas Córdoba",
  },
  {
    key: "footer.aboutDescription",
    label: "Descripción del footer",
    defaultValue:
      "Promoviendo el deporte de bochas en toda la provincia de Córdoba desde 1985.",
  },
  {
    key: "footer.address",
    label: "Dirección",
    defaultValue: "Bulnes 1548, Córdoba Capital, Argentina",
  },
  {
    key: "footer.phone",
    label: "Teléfono",
    defaultValue: "+54 351 423-5678",
  },
  {
    key: "footer.email",
    label: "Correo electrónico",
    defaultValue: "federacionbochascordoba67@gmail.com",
  },
];
const footerKeys = new Set(footerFields.map((field) => field.key));

const seoBaseFields = [
  {
    key: "seo.siteUrl",
    label: "URL pública del sitio",
    defaultValue: "https://bochascba.com.ar",
  },
  {
    key: "seo.defaultImage",
    label: "Imagen predeterminada para compartir",
    defaultValue: "",
  },
  {
    key: "seo.defaultTitle",
    label: "Título SEO general",
    defaultValue: "Federación de Bochas Córdoba",
  },
  {
    key: "seo.defaultDescription",
    label: "Descripción SEO general",
    defaultValue:
      "Sitio oficial de la Federación de Bochas de la Provincia de Córdoba.",
  },
];
const seoBaseKeys = new Set(seoBaseFields.map((field) => field.key));

const seoSections = [
  { key: "home", label: "Home" },
  { key: "noticias", label: "Noticias" },
  { key: "calendario", label: "Calendario" },
  { key: "resultados", label: "Resultados" },
  { key: "federacion", label: "La Federación" },
];

async function load() {
  loading.value = true;
  try {
    const [data, savedVisibility, savedAdminAccess] = await Promise.all([
      listarSiteSettings(),
      obtenerSiteVisibility({ force: true }),
      obtenerAdminAccess({ force: true }),
    ]);
    Object.assign(visibility, savedVisibility);
    Object.assign(adminAccess, savedAdminAccess);
    const settingsByKey = Object.fromEntries(
      (data.items || []).map((item) => [item.key, item.value]),
    );
    footerFields.forEach((field) => {
      form[field.key] = settingsByKey[field.key] || field.defaultValue;
    });
    seoBaseFields.forEach((field) => {
      form[field.key] = settingsByKey[field.key] || field.defaultValue;
    });
    items.value = (data.items || []).filter(
      (item) =>
        !["alojamiento.content", "site.visibility", "admin.access"].includes(item.key) &&
        !footerKeys.has(item.key) &&
        !seoBaseKeys.has(item.key),
    );
    items.value.forEach((item) => {
      form[item.key] = item.value || "";
    });
  } finally {
    loading.value = false;
  }
}

async function save() {
  saving.value = true;
  saved.value = false;
  error.value = "";
  try {
    await Promise.all([
      ...items.value.map((item) =>
        actualizarSiteSetting(item.key, {
          label: item.label,
          group: item.group,
          value: form[item.key],
        }),
      ),
      ...footerFields.map((field) =>
        actualizarSiteSetting(field.key, {
          label: field.label,
          group: "footer",
          value: form[field.key],
        }),
      ),
      ...seoBaseFields.map((field) =>
        actualizarSiteSetting(field.key, {
          label: field.label,
          group: "seo",
          value: form[field.key],
        }),
      ),
      guardarSiteVisibility(visibility),
      guardarAdminAccess(adminAccess),
    ]);
    saved.value = true;
  } catch (_) {
    error.value = "No se pudo guardar la configuración.";
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.admin-input {
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  padding: 0.6rem 0.75rem;
  font-size: 0.875rem;
  color: white;
  outline: none;
}

.admin-input:focus {
  border-color: #a52121;
}

.admin-input option {
  background: #1a1f2e;
  color: #ffffff;
}

.visibility-switch {
  position: relative;
  display: inline-flex;
  width: 2.5rem;
  height: 1.4rem;
  flex: none;
  align-items: center;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  transition: background-color 180ms ease;
}

.visibility-switch__thumb {
  width: 1rem;
  height: 1rem;
  margin-left: 0.2rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  transition: transform 180ms ease, background-color 180ms ease;
}

.peer:checked + .visibility-switch {
  background: #a52121;
}

.peer:checked + .visibility-switch .visibility-switch__thumb {
  transform: translateX(1.1rem);
  background: white;
}
</style>
